import { canAccessPath } from './featureAccess';
import { escapeRegExp } from './searchHighlight';

/**
 * Match a query token against searchable text: substring, light plural/singular, and word overlap.
 * Avoids false positives for very short tokens via word boundaries.
 */
export function flexibleTokenMatch(haystack, token) {
  if (token == null || token === '') return true;
  const h = String(haystack).toLowerCase();
  const t = String(token).toLowerCase();
  if (t.length <= 2) {
    try {
      return new RegExp(`\\b${escapeRegExp(t)}\\b`, 'i').test(h);
    } catch {
      return h.includes(t);
    }
  }
  if (h.includes(t)) return true;
  if (t.length > 3 && t.endsWith('s') && !t.endsWith('ss')) {
    const sing = t.slice(0, -1);
    if (h.includes(sing)) return true;
    if (sing.endsWith('ie') && h.includes(`${sing.slice(0, -2)}y`)) return true;
  }
  if (t.length > 2 && !t.endsWith('s')) {
    if (h.includes(`${t}s`)) return true;
    if (t.endsWith('y') && h.includes(`${t.slice(0, -1)}ies`)) return true;
  }
  if (t.length >= 4) {
    const words = h.split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
    for (const w of words) {
      if (w === t || w.startsWith(t) || t.startsWith(w)) return true;
      if (w.includes(t) || t.includes(w)) return true;
    }
  }
  return false;
}

/**
 * Curated index for global search: routes plus searchable copy (titles, descriptions, keywords).
 * Extend this list as new pages or major UI sections are added.
 */
export const GLOBAL_SEARCH_INDEX = [
  {
    path: '/',
    title: 'Home',
    category: 'General',
    description: 'Post-login home: critical minerals context, alerts, MoU snapshot, semiconductor criticality, and persona paths.',
    keywords: 'home landing start welcome alerts mou semiconductor corporate policy maker',
  },
  {
    path: '/corporate/alerts',
    title: 'Corporate — Alerts',
    category: 'Personas',
    description: 'Corporate command centre: critical mineral alerts and KPIs.',
    keywords: 'corporate alerts command centre supply chain lithium cobalt',
  },
  {
    path: '/corporate',
    title: 'Corporate profile',
    category: 'Personas',
    description: 'Set up or edit your corporate supply chain profile.',
    keywords: 'corporate profile setup company',
  },
  {
    path: '/policy-makers/alerts',
    title: 'Policy Maker — Alerts',
    category: 'Personas',
    description: 'Supply chain alerts for Cabinet and PMO: state actions, market events, and why-now context.',
    keywords: 'policy maker alerts cabinet supply chain state action',
  },
  {
    path: '/policy-makers/import-exposure',
    title: 'Policy Maker — Import exposure',
    category: 'Personas',
    description: 'Strategic import vulnerability matrix and China dependence by sector.',
    keywords: 'policy maker import exposure vulnerability china dependence',
  },
  {
    path: '/policy-makers/trade-agreements',
    title: 'Policy Maker — Trade agreements',
    category: 'Personas',
    description: 'FTA utilisation, allied supply chain frameworks, and strategic trade gaps.',
    keywords: 'policy maker trade agreements FTA allied framework',
  },
  {
    path: '/policy-maker',
    title: 'Policy Maker profile',
    category: 'Personas',
    description: 'Set up or edit policy maker ministry profile.',
    keywords: 'policy maker profile ministry setup',
  },
  {
    path: '/search',
    title: 'Search',
    category: 'General',
    description: 'Find pages, topics, and tools across Stratosphere.',
    keywords: 'search find lookup navigate pages',
  },
  {
    path: '/overview',
    title: 'Overview',
    category: 'Critical minerals',
    description: 'Critical minerals overview: production, reserves, and supply chain snapshot.',
    keywords: 'overview summary metrics lithium cobalt nickel copper rare earth reserves production',
  },
  {
    path: '/supply-markets',
    title: 'Supply & Markets',
    category: 'Critical minerals',
    description: 'Supply chains, market dynamics, and trade flows for critical minerals.',
    keywords: 'supply market trade import export imports exports price volatility commodities',
    sections: [
      'Import Dependency | map import dependency supply chain resilience exposure critical minerals imports',
      'World Reserves | map reserves distribution strategic partnerships resource planning',
      'Trade Restrictions | export bans licenses quotas trade policy import barriers',
      "India's Resource Distribution | domestic reserves production",
      'Global Trade Trends of Critical Minerals | import export time series trade flows',
      'Global Price Trends of Critical Minerals | pricing benchmarks commodities',
      'Mineral → Sector Treemap | downstream industries sectors mineral sector treemap',
      'Trade Policy Tracker | trade policy measures tariffs restrictions import prohibition',
    ],
  },
  {
    path: '/partners-risk',
    title: 'Partners & Risk',
    category: 'Critical minerals',
    description: 'Partner countries, dependency risk, and concentration of supply.',
    keywords: 'partners risk dependency country exposure diversification import imports export trade concentration',
    sections: [
      'Import Partners | partner countries India sourcing trade partners imports',
      'Import Dependency Insights | imported minerals volatility trade value fastest growing',
      'Imports Exports and Dependency Structure | trade balance treemap dependency',
      'Supplier Concentration Treemap | strategic dependence suppliers concentration',
      'Concentration and Market Dependence | diversification market share',
      'Supplier Sophistication and Capability | economic complexity ECI sophistication',
      'Global Opportunity Mapping | opportunities world map',
    ],
  },
  {
    path: '/policy-intelligence',
    title: 'Policy & Intelligence',
    category: 'Critical minerals',
    description: 'Policy developments, regulations, and strategic intelligence for minerals.',
    keywords: 'policy regulation legislation mining export controls sanctions intelligence',
  },
  // {
  //   path: '/global-resource-sentinel',
  //   title: 'Global Resource Sentinel',
  //   category: 'Critical minerals',
  //   description: 'Monitoring signals and resource-related developments globally.',
  //   keywords: 'sentinel monitoring alerts signals resources commodities',
  // },
  // {
  //   path: '/geoeconomic-ai',
  //   title: 'Strategic Intelligence',
  //   category: 'Intelligence',
  //   description: 'AI-enriched geoeconomic events, strategic briefing, narrative analysis, GDELT and news enrichment.',
  //   keywords: 'strategic intelligence geoeconomic ai claude briefing events india outlook predictive analysis enriched pipeline',
  // },
  {
    path: '/event-tracker',
    title: 'Event Tracker',
    category: 'Intelligence',
    description: 'World-map view of geopolitical events from the last 7 days with impact-on-India filtering.',
    keywords: 'event tracker world map geopolitical events dots circles last week impact india critical significant limited filter geoeconomic',
  },
  {
    path: '/intelligence-briefing',
    title: 'Intelligence Briefing',
    category: 'Intelligence',
    description: 'Weekly geoeconomics newsletter hub with policymaker and CFO/CEO briefing editions.',
    keywords: 'intelligence briefing newsletter geoeconomics weekly briefings beijing modi critical minerals',
  },
  // {
  //   path: '/company-risk-intelligence',
  //   title: 'Company Risk Intelligence',
  //   category: 'Risk',
  //   description: 'Company-level financial and risk intelligence, portfolio and sector views.',
  //   keywords: 'company corporate financial risk portfolio sector stratosphere intelligence dashboard',
  // },
  {
    path: '/global-competitiveness',
    title: 'Global Competitiveness',
    category: 'Analytics',
    description: 'Competitiveness indicators and benchmarking across economies and sectors.',
    keywords: 'competitiveness benchmark ranking score global economy',
  },
  {
    path: '/methodology',
    title: 'Methodology',
    category: 'General',
    description: 'How indicators, scores, and data in Stratosphere are defined and calculated.',
    keywords: 'methodology definitions data sources scoring how it works documentation',
  },
  {
    path: '/predictive-alerts',
    title: 'Predictive Alerts',
    category: 'Prediction',
    description: 'Early warning and predictive alerts for supply and policy shocks.',
    keywords: 'alerts prediction early warning forecast notifications',
  },
  {
    path: '/crisis-simulator',
    title: 'Crisis Simulator',
    category: 'Prediction',
    description: 'Simulate crisis scenarios and stress impacts on supply and markets.',
    keywords: 'crisis simulator scenario stress test shock what if',
  },
  {
    path: '/data',
    title: 'Data Explorer',
    category: 'Data',
    description: 'Browse and explore critical minerals datasets and tables.',
    keywords: 'data explorer table csv download browse',
  },
  {
    path: '/critical-minerals-dashboard',
    title: 'Critical Minerals Dashboard',
    category: 'Critical minerals',
    description: 'Full critical minerals dashboard with charts and indicators.',
    keywords: 'dashboard minerals chart analytics',
  },
  {
    path: '/trade-policy-tracker',
    title: 'Trade Policy Tracker',
    category: 'Policy',
    description: 'Track trade policy measures affecting critical minerals and related goods.',
    keywords: 'trade policy tariff quota restriction tracker',
  },
  {
    path: '/critical-mineral-policies',
    title: 'Critical Mineral Policies',
    category: 'Policy',
    description: 'Table of policies affecting critical minerals by jurisdiction.',
    keywords: 'policies table regulation country jurisdiction',
  },
  {
    path: '/partner-risk-table',
    title: 'Partner Risk Table',
    category: 'Risk',
    description: 'Tabular partner and supplier risk metrics.',
    keywords: 'partner risk table supplier score',
  },
  {
    path: '/data-upload',
    title: 'Data Upload',
    category: 'Data',
    description: 'Upload datasets and files for analysis workflows.',
    keywords: 'upload file dataset csv excel spreadsheet bulk ingest',
  },
  {
    path: '/hs-codes',
    title: 'HS Codes',
    category: 'Data',
    description: 'Harmonized System codes relevant to minerals and related products.',
    keywords: 'hs code harmonized customs commodity classification',
  },
  {
    path: '/executive-summary',
    title: 'Executive Summary',
    category: 'Reports',
    description: 'High-level executive summary of risks and opportunities.',
    keywords: 'executive summary report leadership briefing',
  },
  {
    path: '/countries-situation-data',
    title: 'Countries Situation Data',
    category: 'Data',
    description: 'Country-level situation and context data for minerals and geopolitics.',
    keywords: 'countries country situation geography geopolitical',
  },
  {
    path: '/ev-dashboard',
    title: 'EV Dashboard',
    category: 'EV',
    description: 'Electric vehicle market and battery supply chain dashboard.',
    keywords: 'ev electric vehicle battery lithium nickel cobalt charging',
  },
  {
    path: '/ev_crisis_simulator',
    title: 'EV Crisis Simulator',
    category: 'EV',
    description: 'Scenario simulation for EV and battery supply disruptions.',
    keywords: 'ev crisis simulator battery supply disruption',
  },
  {
    path: '/admin/users',
    title: 'User Management',
    category: 'Administration',
    description: 'Manage users, roles, and subscription features (super admin).',
    keywords: 'admin users roles permissions subscription super admin',
    requiresSuperAdmin: true,
  },
];

function haystackForItem(item) {
  const sec = Array.isArray(item.sections) ? item.sections.join(' ') : '';
  return [
    item.title,
    item.category,
    item.description,
    item.keywords,
    item.path,
    sec,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function scoreItem(item, tokens) {
  const title = (item.title || '').toLowerCase();
  const path = (item.path || '').toLowerCase();
  const desc = (item.description || '').toLowerCase();
  const kw = (item.keywords || '').toLowerCase();
  const secText = Array.isArray(item.sections) ? item.sections.join(' ').toLowerCase() : '';
  const hay = haystackForItem(item);
  let score = 0;
  for (const t of tokens) {
    if (!t) continue;
    if (!flexibleTokenMatch(hay, t)) return 0;
    if (flexibleTokenMatch(title, t)) score += 12;
    else if (flexibleTokenMatch(path, t)) score += 7;
    else if (flexibleTokenMatch(desc, t)) score += 5;
    else if (flexibleTokenMatch(secText, t)) score += 4;
    else if (flexibleTokenMatch(kw, t)) score += 3;
    else score += 1;
  }
  return score;
}

/**
 * @param {string} query
 * @param {string[]} features
 * @param {boolean} isSuperAdmin
 * @param {{ limit?: number, context?: 'ev' | 'default' }} options
 */
export function filterGlobalSearch(query, features, isSuperAdmin, options = {}) {
  const limit = options.limit ?? 24;
  const context = options.context ?? 'default';
  const user = options.user;
  const accessible = GLOBAL_SEARCH_INDEX.filter((item) => {
    if (item.requiresSuperAdmin && !isSuperAdmin) return false;
    if (!canAccessPath(item.path, features, user)) return false;
    if (context === 'ev') {
      // Keep search results scoped to EV flow only.
      return item.path.startsWith('/ev');
    }
    return true;
  });

  const q = (query || '').trim().toLowerCase();
  const tokens = q ? q.split(/\s+/).filter(Boolean) : [];

  if (tokens.length === 0) {
    return accessible.slice(0, 12);
  }

  return accessible
    .map((item) => ({ item, score: scoreItem(item, tokens) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
    .slice(0, limit)
    .map((x) => x.item);
}
